/**
 * t363u (security audit #8): the file types the Hub accepts as uploads, an allow-list shared by the API (which also
 * checks the real bytes) and the web, phone web and iOS pickers (accept lists and an early, friendly refusal).
 *
 * Photos, PDFs, Office documents, CSV / plain text and videos. No audio: the Hub has no voice notes (call recordings
 * are made server-side). Anything else, including HTML, SVG, archives and programs, is refused.
 */
export type UploadKind = 'image' | 'video' | 'pdf' | 'office' | 'text';
export interface UploadType { mime: string; ext: readonly string[]; kind: UploadKind; label: string }

export const UPLOAD_TYPES: readonly UploadType[] = [
  { mime: 'image/jpeg', ext: ['jpg', 'jpeg', 'jpe'], kind: 'image', label: 'JPEG' },
  { mime: 'image/png', ext: ['png'], kind: 'image', label: 'PNG' },
  { mime: 'image/gif', ext: ['gif'], kind: 'image', label: 'GIF' },
  { mime: 'image/webp', ext: ['webp'], kind: 'image', label: 'WebP' },
  { mime: 'image/heic', ext: ['heic'], kind: 'image', label: 'HEIC' },
  { mime: 'image/heif', ext: ['heif'], kind: 'image', label: 'HEIF' },
  { mime: 'application/pdf', ext: ['pdf'], kind: 'pdf', label: 'PDF' },
  { mime: 'application/msword', ext: ['doc'], kind: 'office', label: 'Word' },
  { mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', ext: ['docx'], kind: 'office', label: 'Word' },
  { mime: 'application/vnd.ms-excel', ext: ['xls'], kind: 'office', label: 'Excel' },
  { mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', ext: ['xlsx'], kind: 'office', label: 'Excel' },
  { mime: 'application/vnd.ms-powerpoint', ext: ['ppt'], kind: 'office', label: 'PowerPoint' },
  { mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', ext: ['pptx'], kind: 'office', label: 'PowerPoint' },
  { mime: 'text/csv', ext: ['csv'], kind: 'text', label: 'CSV' },
  { mime: 'text/plain', ext: ['txt', 'text', 'log'], kind: 'text', label: 'text' },
  { mime: 'video/mp4', ext: ['mp4', 'm4v'], kind: 'video', label: 'MP4' },
  { mime: 'video/quicktime', ext: ['mov', 'qt'], kind: 'video', label: 'MOV' },
  { mime: 'video/webm', ext: ['webm'], kind: 'video', label: 'WebM' },
];

/** Other names browsers and pickers use for the same types (Windows calls a .csv application/vnd.ms-excel, etc.). */
const ALIASES: Record<string, string> = {
  'image/jpg': 'image/jpeg', 'image/pjpeg': 'image/jpeg', 'image/heic-sequence': 'image/heic', 'image/heif-sequence': 'image/heif',
  'application/x-pdf': 'application/pdf', 'text/comma-separated-values': 'text/csv', 'application/csv': 'text/csv', 'text/x-csv': 'text/csv',
  'video/x-m4v': 'video/mp4', 'video/x-quicktime': 'video/quicktime',
};

/** The one sentence every front end shows when a file isn't allowed. */
export const UPLOAD_REFUSED = "This type of file can't be uploaded. You can add photos (JPEG, PNG, GIF, WebP, HEIC), PDFs, Word, Excel and PowerPoint files, CSV or text files, and videos (MP4, MOV, WebM).";

/** For <input type="file" accept>: extensions and types of everything above. */
export const UPLOAD_ACCEPT = [...UPLOAD_TYPES.flatMap(t => t.ext.map(e => `.${e}`)), ...UPLOAD_TYPES.map(t => t.mime)].join(',');
/** For pickers that take MIME types only (expo-document-picker). */
export const UPLOAD_MIME_TYPES = UPLOAD_TYPES.map(t => t.mime);

const baseMime = (m: string | null | undefined) => (m ?? '').split(';')[0].trim().toLowerCase();
export const uploadTypeByMime = (mime: string | null | undefined): UploadType | null => {
  const m = baseMime(mime);
  return UPLOAD_TYPES.find(t => t.mime === (ALIASES[m] ?? m)) ?? null;
};
export const uploadExtension = (fileName: string | null | undefined) => {
  const m = /\.([a-z0-9]{1,8})$/i.exec((fileName ?? '').trim());
  return m ? m[1].toLowerCase() : null;
};

/**
 * The allowed type a file would be stored as, or null if it isn't allowed. The extension decides when there is one
 * (a .html declared as image/jpeg is refused); a name without an extension falls back to the declared type.
 * The API then checks the uploaded bytes really are that kind of file.
 */
export function uploadTypeOf(fileName: string | null | undefined, declared?: string | null): UploadType | null {
  const ext = uploadExtension(fileName);
  if (ext) {
    const byExt = UPLOAD_TYPES.find(t => t.ext.includes(ext));
    if (byExt) return byExt;
    // An extension we don't know (".final", ".v2") is only fine when the declared type is allowed and the name isn't
    // a known-dangerous one.
    if (RISKY_EXT.has(ext)) return null;
  }
  return uploadTypeByMime(declared);
}

const RISKY_EXT = new Set(['html', 'htm', 'xhtml', 'shtml', 'svg', 'svgz', 'xml', 'xsl', 'js', 'mjs', 'json', 'exe', 'msi', 'bat', 'cmd',
  'com', 'scr', 'sh', 'ps1', 'vbs', 'jar', 'apk', 'app', 'dmg', 'pkg', 'zip', 'rar', '7z', 'gz', 'tar', 'swf', 'php', 'py', 'rb', 'pl',
  'docm', 'xlsm', 'pptm', 'dotm', 'xltm', 'potm', 'ppsm', 'hta', 'lnk', 'reg', 'iso', 'img', 'dll', 'so', 'mht', 'mhtml', 'url', 'webloc']);

/** True when a picked file would be refused, so the front end can say so before uploading anything. */
export const uploadRefused = (fileName: string | null | undefined, declared?: string | null) => uploadTypeOf(fileName, declared) === null;

/** Types the browser may show in the page (everything else is served as a download). */
export const isInlineUploadType = (mime: string | null | undefined) => {
  const t = uploadTypeByMime(mime);
  return Boolean(t && (t.kind === 'image' || t.kind === 'video'));
};
